// normalizeAskResult(raw): every shape the Ask route can deliver, as ONE turn-result
// view model for the UI (investigating-analyst Task 13).
//
// Accepts, identically:
//   - the inline HTTP 200 body of POST /proxy/checks/agent/ask (and /checks/ask);
//   - a finished job's `result` (the WS `checks_ask_job` push / the poll), or the
//     whole job record `{ jobId, status, result }`;
//   - the legacy ASK_INVESTIGATE=off result (markdown only, usage {input, output,
//     cacheRead, cacheCreate});
//   - ok:false / degraded fallbacks, partial or cap-hit results, usage === null.
//
// Never throws; always returns every field with a safe default. The server masks ID
// values (askDelivery.js), so nothing here masks. Shape cases that pin this live in
// ./fixtures/answerShapeCases.json (byte mirror of the proxy file, see
// proxy-server/test/answerShapeParity.test.js).
//
// View model:
//   { kind:'legacy'|'investigation', ok, error, answer, markdown, evidence[], next[],
//     followUps[], usage|null, partial, capHit, degraded, trail[], scope|null, guards[] }
// usage, when present, is always { inputTokens, outputTokens, totalTokens, cap, capHit };
// the legacy usage shape is mapped onto it with totalTokens = input + output (the sum the
// daily cap counts; cache tokens are not added) and cap null.

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const text = (v) => (typeof v === 'string' ? v : '');
const finite = (v) => typeof v === 'number' && Number.isFinite(v);

function emptyView() {
  return {
    kind: 'legacy', ok: true, error: '', answer: '', markdown: '', evidence: [], next: [],
    followUps: [], usage: null, partial: false, capHit: false, degraded: false,
    trail: [], scope: null, guards: [],
  };
}

function strings(list) {
  return Array.isArray(list) ? list.filter((s) => typeof s === 'string' && s) : [];
}

function evidenceOf(list) {
  const out = [];
  if (!Array.isArray(list)) return out;
  for (const e of list) {
    if (!isObj(e)) continue;
    const endpoint = text(e.endpoint);
    const field = text(e.field);
    if (!endpoint && !field) continue;
    const v = e.value;
    const item = {
      endpoint,
      field,
      value: (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') ? String(v) : '',
    };
    if (typeof e.rowRef === 'string' || finite(e.rowRef)) item.rowRef = e.rowRef;
    if (typeof e.requestId === 'string' && e.requestId.trim() && e.requestId.length <= 120) item.requestId = e.requestId;
    out.push(item);
  }
  return out;
}

function trailOf(list) {
  const out = [];
  if (!Array.isArray(list)) return out;
  for (const e of list) {
    if (!isObj(e) || typeof e.tool !== 'string' || !e.tool) continue;
    const item = { tool: e.tool, summary: text(e.summary) };
    if (typeof e.ts === 'string' || finite(e.ts)) item.ts = e.ts;
    out.push(item);
  }
  return out;
}

const isLegacyUsage = (u) => isObj(u) && !finite(u.totalTokens) && (finite(u.input) || finite(u.output));

function usageOf(u) {
  if (!isObj(u)) return null;
  if (isLegacyUsage(u)) {
    const inputTokens = finite(u.input) ? u.input : 0;
    const outputTokens = finite(u.output) ? u.output : 0;
    return { inputTokens, outputTokens, totalTokens: inputTokens + outputTokens, cap: null, capHit: false };
  }
  if (!['inputTokens', 'outputTokens', 'totalTokens'].some((k) => finite(u[k]))) return null;
  const inputTokens = finite(u.inputTokens) ? u.inputTokens : 0;
  const outputTokens = finite(u.outputTokens) ? u.outputTokens : 0;
  return {
    inputTokens,
    outputTokens,
    totalTokens: finite(u.totalTokens) ? u.totalTokens : inputTokens + outputTokens,
    cap: finite(u.cap) ? u.cap : null,
    capHit: u.capHit === true,
  };
}

function scopeOf(s) {
  if (!isObj(s)) return null;
  const out = {};
  if (typeof s.usb === 'boolean') out.usb = s.usb;
  if (typeof s.bodiesReadable === 'boolean') out.bodiesReadable = s.bodiesReadable;
  if (typeof s.note === 'string' && s.note) out.note = s.note;
  if (finite(s.bodyUnavailable)) out.bodyUnavailable = s.bodyUnavailable;
  return out;
}

// A whole job record `{ jobId, status, result }` becomes its result; a failed job
// (no result) keeps its status/error so the caller sees ok:false. The inline route
// body also has a `result` (a check result), so unwrap only when there is no answer
// string and the object looks like a job.
function unwrapJob(raw) {
  const looksLikeJob = typeof raw.jobId === 'string' || typeof raw.status === 'string';
  if (looksLikeJob && typeof raw.answer !== 'string' && isObj(raw.result)) return raw.result;
  return raw;
}

export function normalizeAskResult(rawIn) {
  try {
    if (!isObj(rawIn)) return emptyView();
    const raw = unwrapJob(rawIn);
    const view = emptyView();

    const failedJob = raw === rawIn && typeof rawIn.status === 'string'
      && ['error', 'failed', 'cancelled', 'canceled'].includes(rawIn.status);
    view.ok = raw.ok !== false && !failedJob;
    view.error = text(raw.error) || text(raw.detail);

    const usage = usageOf(raw.usage);
    const hasShape = Array.isArray(raw.evidence) || Array.isArray(raw.trail);
    view.kind = (isLegacyUsage(raw.usage) || !hasShape) ? 'legacy' : 'investigation';

    view.answer = text(raw.answer);
    view.markdown = text(raw.markdown) || view.answer;
    view.evidence = evidenceOf(raw.evidence);
    view.next = strings(raw.next);
    view.followUps = strings(raw.followUps);
    view.usage = usage;
    view.capHit = raw.capHit === true || (usage ? usage.capHit : false);
    view.partial = raw.partial === true || view.capHit;
    view.degraded = raw.degraded === true;
    view.trail = trailOf(raw.trail);
    view.scope = scopeOf(raw.scope);
    view.guards = strings(raw.guards);
    return view;
  } catch {
    return emptyView();
  }
}

export default normalizeAskResult;
